import { z } from "zod";
/** External query sources. Source rows are not ingested into the warehouse. */
export const databaseProviderSchema = z.enum(["azure-sql", "bigquery"]);
export const databaseProviderLabels = {
    "azure-sql": "Azure SQL", bigquery: "BigQuery",
};
const id = z.string().trim().min(1).max(200);
const sqlIdentifier = "[A-Za-z_][A-Za-z0-9_$]{0,127}";
export const databaseRelationSchema = z.string().trim().max(400)
    .regex(new RegExp(`^(?:[a-z][a-z0-9-]{4,61}[a-z0-9]\\.)?${sqlIdentifier}\\.${sqlIdentifier}$`));
const project = z.string().regex(/^[a-z][a-z0-9-]{4,61}[a-z0-9]$/u);
export const AZURE_SQL_MAX_RELATIONS = 1000;
const relations = z.array(databaseRelationSchema).min(1).max(100)
    .refine(values => new Set(values).size === values.length, "Remove duplicate relations");
export const databaseConfigSchema = z.discriminatedUnion("provider", [
    z.object({
        provider: z.literal("azure-sql"),
        host: z.string().trim().toLowerCase().regex(/^[a-z0-9-]+\.database\.windows\.net$/u),
        database: z.string().trim().min(1).max(128),
        relationSelection: z.enum(["selected", "all-readable"]).optional(),
        allowedRelations: z.array(databaseRelationSchema).max(AZURE_SQL_MAX_RELATIONS)
            .refine(values => new Set(values).size === values.length, "Remove duplicate relations")
            .refine(values => values.every(value => value.split(".").length === 2), "Use schema.table names"),
    }).strict().refine(value => value.relationSelection === "all-readable" || value.allowedRelations.length > 0, { message: "Choose at least one table or view", path: ["allowedRelations"] }),
    z.object({
        provider: z.literal("bigquery"),
        projectId: project,
        location: z.string().trim().min(2).max(60).regex(/^[A-Za-z0-9-]+$/u),
        maximumBytesBilled: z.number().int().min(1_000_000).max(1_000_000_000_000),
        allowedRelations: relations.refine(values => values.every(value => value.split(".").length === 3), "Use project.dataset.table names"),
    }).strict(),
]);
export const databaseCredentialsSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("sql-password"), username: id, password: z.string().min(1).max(20_000) }).strict(),
    z.object({
        type: z.literal("entra-secret"), tenantId: z.uuid(), clientId: z.uuid(),
        clientSecret: z.string().min(1).max(20_000),
    }).strict(),
    z.object({
        type: z.literal("service-account"),
        clientEmail: z.email().regex(/\.iam\.gserviceaccount\.com$/u),
        privateKey: z.string().min(1).max(20_000).refine(value => value.startsWith("-----BEGIN PRIVATE KEY-----") && value.includes("-----END PRIVATE KEY-----"), "Use a service-account private key"),
    }).strict(),
]);
export const databaseConnectionSchema = z.object({
    id, organisationId: id, displayName: z.string().trim().min(1).max(100),
    config: databaseConfigSchema, credentialType: z.enum(["sql-password", "entra-secret", "service-account"]),
    credentialRevision: z.number().int().positive(),
    status: z.enum(["pending", "healthy", "error"]),
    lastVerifiedAt: z.number().int().nonnegative().nullable(),
    lastErrorCode: z.string().nullable(),
    createdAt: z.number().int().nonnegative(), updatedAt: z.number().int().nonnegative(),
}).strict();
export const listDatabaseConnectionsResponseSchema = z.object({ connections: z.array(databaseConnectionSchema) }).strict();
export function databaseCredentialsMatch(config, credentials) {
    return (config.provider === "bigquery") === (credentials.type === "service-account");
}
export const createDatabaseConnectionRequestSchema = z.object({
    organisationId: id, displayName: z.string().trim().min(1).max(100),
    config: databaseConfigSchema, credentials: databaseCredentialsSchema,
}).strict().refine(value => databaseCredentialsMatch(value.config, value.credentials), {
    message: "Credentials do not match the database provider", path: ["credentials"],
});
export const databaseConnectionIdentitySchema = z.object({ organisationId: id, connectionId: id }).strict();
export const updateDatabaseConnectionRequestSchema = z.object({
    displayName: z.string().trim().min(1).max(100), config: databaseConfigSchema,
    credentials: databaseCredentialsSchema.optional(),
}).strict();
export const databaseReadRequestSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("relations") }).strict(),
    z.object({ operation: z.literal("describe"), relation: databaseRelationSchema }).strict(),
    z.object({
        operation: z.literal("query"), sql: z.string().trim().min(1).max(50_000),
        rowLimit: z.number().int().min(1).max(200).default(100),
    }).strict(),
]);
export const resolvedDatabaseConnectionSchema = z.object({
    tenantSlug: id, connection: databaseConnectionSchema, credentials: databaseCredentialsSchema,
}).strict().refine(value => databaseCredentialsMatch(value.connection.config, value.credentials), {
    message: "Credentials do not match the database provider",
});
